export const MEDIA_TYPE_VIDEO = 'video' as const;
export const MEDIA_TYPE_STREAM = 'stream' as const;
export const MEDIA_TYPE_IMAGE = 'image' as const;
export const MEDIA_TYPE_AUDIO = 'audio' as const;
export const MEDIA_TYPE_PDF = 'pdf' as const;
export const MEDIA_TYPE_NLE_PROJECT = 'nle_project' as const;
export const MEDIA_TYPE_CUSTOM = 'custom' as const;
export const MEDIA_TYPE_PLACEHOLDER = 'placeholder' as const;
export const MEDIA_TYPE_LINK = 'link' as const;
export const MEDIA_TYPE_SEQUENCE = 'sequence' as const;
export const MEDIA_TYPE_FILE = 'file' as const;
export const MEDIA_TYPE_COLLECTION = 'collection' as const;
export const MEDIA_TYPE_COLLECTION_ISG = 'collection_isg' as const;
export const MEDIA_TYPE_SUBCLIP = 'subclip' as const;
export const MEDIA_TYPE_PLAYLIST = 'playlist' as const;
export const MEDIA_TYPE_POST = 'post' as const;

export type MediaType =
  | typeof MEDIA_TYPE_VIDEO
  | typeof MEDIA_TYPE_STREAM
  | typeof MEDIA_TYPE_IMAGE
  | typeof MEDIA_TYPE_AUDIO
  | typeof MEDIA_TYPE_PDF
  | typeof MEDIA_TYPE_NLE_PROJECT
  | typeof MEDIA_TYPE_CUSTOM
  | typeof MEDIA_TYPE_PLACEHOLDER
  | typeof MEDIA_TYPE_LINK
  | typeof MEDIA_TYPE_SEQUENCE
  | typeof MEDIA_TYPE_FILE
  | typeof MEDIA_TYPE_COLLECTION
  | typeof MEDIA_TYPE_COLLECTION_ISG
  | typeof MEDIA_TYPE_SUBCLIP
  | typeof MEDIA_TYPE_PLAYLIST
  | typeof MEDIA_TYPE_POST;

// We are getting proxy format from MediaInfo, to unify it we compare them in uppercase
export const allowedImageProxyFormatsSet = new Set([
  'PNG',
  'JPEG',
  'JPG',
  'GIF',
  'BMP',
  'WEBP',
]);
export const allowedVideoProxyFormatsSet = new Set(['MPEG-4']);
export const allowedStreamProxyFormatsSet = new Set(['M3U8', 'MPD']);
export const allowedAudioProxyFormatsSet = new Set([
  'MPEG AUDIO',
  'MP3',
  'M4A',
]);

// Typed as MediaType[] so adding a non-MediaType is a compile error, which is
// what lets isGenericMediaType narrow. The set stays wide because callers test
// raw values coming off the API.
const genericMediaTypesList: readonly MediaType[] = [
  MEDIA_TYPE_VIDEO,
  MEDIA_TYPE_STREAM,
  MEDIA_TYPE_IMAGE,
  MEDIA_TYPE_AUDIO,
  MEDIA_TYPE_PDF,
];

export const genericMediaTypes: ReadonlySet<string | undefined> = new Set(
  genericMediaTypesList,
);

export const isGenericMediaType = (
  value: string | undefined,
): value is MediaType => genericMediaTypes.has(value);

// Media types the player has a renderer for. Subclips play through their
// parent's proxy, so they resolve to their own media type rather than a generic
// one and have to be listed separately
export const playableMediaTypes = new Set<string | undefined>([
  ...genericMediaTypes,
  MEDIA_TYPE_SUBCLIP,
]);

export const seekableMediaTypes = new Set<string | undefined>([
  MEDIA_TYPE_VIDEO,
  MEDIA_TYPE_STREAM,
  MEDIA_TYPE_AUDIO,
]);

// Streams are excluded: a subclip plays its parent's proxy clamped to a time
// range, which manifest-based proxies don't support
export const subclippableMediaTypes = new Set<string | undefined>([
  MEDIA_TYPE_VIDEO,
  MEDIA_TYPE_AUDIO,
]);

export const graphicalMediaTypes = new Set<string | undefined>([
  MEDIA_TYPE_VIDEO,
  MEDIA_TYPE_STREAM,
  MEDIA_TYPE_IMAGE,
]);

/**
 * The thumbnail label for items that aren't ordinary media
 * (placeholder / link / unknown). Returns `undefined` for actual media types, so
 * the caller can supply its own format/extension fallback (`?? …`).
 */
export function nonMediaItemLabel(
  mediaType: string | null | undefined,
): string | undefined {
  if (mediaType === MEDIA_TYPE_PLACEHOLDER) {
    return 'Placeholder';
  }
  if (mediaType === 'unknown') {
    return 'Unknown';
  }
  if (mediaType === MEDIA_TYPE_LINK) {
    return 'Link';
  }
  return undefined;
}
